export type CrmTaskPriority = 'HIGH' | 'MEDIUM' | 'LOW';
export type CrmTaskEntityType = 'lead' | 'applicant';
export type CrmTaskStatus = 'NEW' | 'IN_PROGRESS' | 'COMPLETED';

export const CRM_TASK_PRIORITY_LABELS: Record<CrmTaskPriority, string> = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

export type AssignedTaskListItem = {
  id: string;
  entityType: CrmTaskEntityType;
  entityId: string;
  entityDisplayName: string;
  entityRoleLabel: 'Lead' | 'Applicant';
  title: string;
  description?: string;
  priority: CrmTaskPriority;
  priorityLabel: string;
  status: CrmTaskStatus;
  statusLabel: string;
  dueAt?: string;
};

export type PlatformTaskReminder = {
  dueTodayCount: number;
  overdueCount: number;
  urgentTasks: AssignedTaskListItem[];
};

export function buildAssignedTaskDetailRoute(task: AssignedTaskListItem): string[] {
  const base = task.entityType === 'lead' ? '/platform/leads' : '/platform/applicants';
  return [base, task.entityId];
}

export function buildAssignedTaskDetailQuery(task: AssignedTaskListItem): { taskId: string } {
  return { taskId: task.id };
}

export function entityInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export function taskDueUrgency(
  dueAt: string | undefined,
  now = new Date(),
): 'overdue' | 'due-soon' | 'upcoming' | 'none' {
  if (!dueAt) return 'none';

  const due = new Date(dueAt);
  if (Number.isNaN(due.getTime())) return 'none';
  if (due.getTime() < now.getTime()) return 'overdue';

  const hoursUntil = (due.getTime() - now.getTime()) / 3_600_000;
  if (hoursUntil <= 24) return 'due-soon';

  return 'upcoming';
}

export function isDisplayableAssignedTask(task: AssignedTaskListItem | null | undefined): boolean {
  if (!task?.id?.trim()) {
    return false;
  }

  if (!task.title?.trim() || !task.entityDisplayName?.trim()) {
    return false;
  }

  if (!task.priority || !task.status || !task.statusLabel?.trim()) {
    return false;
  }

  return true;
}

export function isUrgentAssignedTask(task: AssignedTaskListItem | null | undefined): boolean {
  return isDisplayableAssignedTask(task) && Boolean(task?.dueAt);
}

export function buildTaskReminderMessage(dueTodayCount: number, overdueCount: number): string {
  const parts: string[] = [];

  if (dueTodayCount > 0) {
    parts.push(`${dueTodayCount} task${dueTodayCount === 1 ? '' : 's'} due today`);
  }

  if (overdueCount > 0) {
    parts.push(`${overdueCount} overdue task${overdueCount === 1 ? '' : 's'}`);
  }

  if (parts.length === 0) {
    return "You're all caught up! No overdue or due today tasks.";
  }

  return `You have ${parts.join(' and ')}. Please review and complete them on time.`;
}
